import React, { useState, useEffect } from 'react';
import adminService from '../../services/adminService';
import '../../styles/components/admin/AdminSwitchPrimaryDoctorModal.css';
import logoImage from '../../assets/images/Neurolex_Logo_New.png';

const SwitchPrimaryDoctorModal = ({ 
  isOpen, 
  onClose, 
  patient, 
  currentDoctor,
  tenantId, 
  onDoctorChanged 
}) => {
  const [loading, setLoading] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [confirmModalOpen, setConfirmModalOpen] = useState(true);
  const [selectModalOpen, setSelectModalOpen] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Reset state when modal is opened
  useEffect(() => {
    if (isOpen) {
      setConfirmModalOpen(true);
      setSelectModalOpen(false);
      setSelectedDoctor('');
      setError(null);
      setSuccess(false);
    }
  }, [isOpen]);

  // Fetch available doctors when selection modal is opened
  useEffect(() => {
    if (isOpen && selectModalOpen) {
      fetchAvailableDoctors();
    }
  }, [isOpen, selectModalOpen, tenantId]);

  const fetchAvailableDoctors = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Call admin service to fetch available doctors
      const response = await adminService.getApprovedDoctors({ 
        tenantId, 
        status: 'active',
        limit: 100 
      });
      
      if (response.success) {
        // Filter out the current doctor if provided
        const filteredDoctors = currentDoctor 
          ? response.data.filter(doc => doc._id !== currentDoctor._id)
          : response.data;
        
        setDoctors(filteredDoctors || []);
      } else {
        setError('Failed to load available doctors');
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching available doctors:', err);
      setError('Failed to load doctor list');
      setLoading(false);
    }
  };

  const handleConfirmSwitch = () => {
    setConfirmModalOpen(false);
    setSelectModalOpen(true);
  };

  const handleSaveAndUpdate = async () => {
  if (!selectedDoctor) {
    setError('Please select a doctor');
    return;
  }

  try {
    setLoading(true);
    setError(null);
    
    // Get tenant ID (from props or patient object)
    const effectiveTenantId = tenantId || patient.tenantId;
    
    if (!effectiveTenantId) {
      setError('Tenant ID is required but not available');
      setLoading(false);
      return;
    }

    // Step 1: Get all associations for this patient
    const associationsResponse = await adminService.getPatientDoctorAssociations(
      patient._id, 
      effectiveTenantId
    );
    
    // Step 2: Delete all existing associations
    if (associationsResponse.success && associationsResponse.data) {
      const existingAssociations = associationsResponse.data;
      
      console.log(`Found ${existingAssociations.length} associations to remove`);
      
      // Delete all existing associations
      for (const assoc of existingAssociations) {
        await adminService.deletePatientDoctorAssociation(
          assoc._id,
          effectiveTenantId
        );
      }
    }
    
    // Step 3: Create a new association with the selected doctor
    const createResponse = await adminService.createPatientDoctorAssociation(
      patient._id,
      selectedDoctor,
      'active',
      effectiveTenantId
    );
    
    if (!createResponse.success) {
      throw new Error(createResponse.message || 'Failed to create doctor association');
    }
    
    setSuccess(true);
    
    // Refresh data and close modal
    setTimeout(() => {
      if (onDoctorChanged) {
        onDoctorChanged();
      }
      onClose();
    }, 1500);
    
    setLoading(false);
  } catch (err) {
    console.error('Error switching primary doctor:', err);
    setError(err.message || 'Failed to switch primary doctor');
    setLoading(false);
  }
};

  if (!isOpen) return null;

  return (
    <div className="switch-doctor-modal-overlay">
      {/* Confirmation Modal */}
      {confirmModalOpen && (
        <div className="switch-doctor-modal-container">
          <div className="switch-doctor-modal-logo">
            <img src={logoImage} alt="System Logo" className="switch-doctor-logo-img" />
          </div>
          
          <div className="switch-doctor-modal-content">
            <h3 className="switch-doctor-modal-title">SWITCH PRIMARY CARER?</h3>
            
            <div className="switch-doctor-patient-info">
              <div className="switch-doctor-patient-details-box">
              <p className="switch-doctor-info-item">
                <strong>Patient:</strong> {patient.firstName} {patient.lastName}
              </p>
              {currentDoctor && (
                <p className="switch-doctor-info-item">
                  <strong>Current Doctor:</strong> Dr. {currentDoctor.firstName} {currentDoctor.lastName}
                </p>
              )}
              </div>
              <div className="switch-doctor-warning-box">
                <p className="switch-doctor-warning-text">
                  This action will revoke the doctor's access to the patient's records, session history, and ongoing care. Please confirm to proceed.
                </p>
              </div>
            </div>
            
            <div className="switch-doctor-modal-actions">
              <button 
                className="switch-doctor-btn switch-doctor-btn-cancel"
                onClick={onClose}
              >
                Cancel
              </button>
              <button 
                className="switch-doctor-btn switch-doctor-btn-confirm"
                onClick={handleConfirmSwitch}
              >
                Confirm & Switch
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Doctor Selection Modal */}
      {selectModalOpen && (
        <div className="switch-doctor-modal-container">
          <div className="switch-doctor-modal-logo">
            <img src={logoImage} alt="System Logo" className="switch-doctor-logo-img" />
          </div>
          
          <div className="switch-doctor-modal-content">
            <h3 className="switch-doctor-modal-title">CHOOSE A NEW PRIMARY CARER</h3>
            
            <p className="switch-doctor-selection-info">
              Assign a new doctor to take over care for this patient.
            </p>
            
            <div className="switch-doctor-patient-details">
              <p className="switch-doctor-info-item">
                <strong>Patient:</strong> {patient.firstName} {patient.lastName}
              </p>
            </div>
            
            {error && (
              <div className="switch-doctor-alert switch-doctor-alert-error">
                <span className="switch-doctor-alert-icon">⚠️</span>
                <span className="switch-doctor-alert-message">{error}</span>
              </div>
            )}
            
            {success && (
              <div className="switch-doctor-alert switch-doctor-alert-success">
                <span className="switch-doctor-alert-icon">✓</span>
                <span className="switch-doctor-alert-message">Doctor successfully changed!</span>
              </div>
            )}
            
            <div className="switch-doctor-form-group">
              <label htmlFor="switchDoctorSelect" className="switch-doctor-form-label">
                Doctor:
              </label>
              <select
                id="switchDoctorSelect"
                className="switch-doctor-form-select"
                value={selectedDoctor}
                onChange={(e) => setSelectedDoctor(e.target.value)}
                disabled={loading || success}
              >
                <option value="">Select a doctor</option>
                {doctors.map(doctor => (
                  <option key={doctor._id} value={doctor._id}>
                    Dr. {doctor.firstName} {doctor.lastName} - {doctor.specialty || doctor.specialization || 'General'}
                  </option>
                ))}
              </select>
            </div>
            
            {loading && (
              <div className="switch-doctor-loading-container">
                <div className="switch-doctor-loading-spinner"></div>
                <span className="switch-doctor-loading-text">Processing...</span>
              </div>
            )}
            
            <div className="switch-doctor-modal-actions">
              <button 
                className="switch-doctor-btn switch-doctor-btn-cancel"
                onClick={onClose}
                disabled={loading || success}
              >
                Cancel
              </button>
              <button 
                className="switch-doctor-btn switch-doctor-btn-primary"
                onClick={handleSaveAndUpdate}
                disabled={!selectedDoctor || loading || success}
              >
                Save & Update
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SwitchPrimaryDoctorModal;